import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Divider,
  Chip,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Drawer,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Description,
  AccountBalanceWallet,
  Api,
  Settings,
  Assessment,
  SupportAgent,
  Person,
  Logout,
  Warning,
  AccountCircle,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getMerchantProfile } from '../services/api';
import { showSuccessToast, showErrorToast, showInfoToast } from '../utils/toast';

const SimpleMerchantLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = React.useState(null);
  const [profileData, setProfileData] = React.useState(null);

  // Ensure all default values are strings to avoid capitalize errors
  const safeUser = {
    merchantName: user?.merchantName || "",
    name: user?.name || "",
    email: user?.email || "",
    merchantId: user?.merchantId || "",
    kycStatus: user?.kycStatus || "PENDING"
  };

  const fetchProfileData = async () => {
    try {
      const response = await getMerchantProfile();
      setProfileData(response.data);
      // Removed toast to avoid showing on every page load
    } catch (err) {
      console.error('Failed to fetch profile data:', err);
      showErrorToast('Failed to load profile data');
    }
  };

  const handleProfileMenuOpen = async (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  React.useEffect(() => {
    fetchProfileData();
  }, []);

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleProfileClick = () => {
    navigate('/merchant/profile');
    showInfoToast('Navigating to profile page');
    handleProfileMenuClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    showSuccessToast('Logged out successfully');
    handleProfileMenuClose();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isPendingKyc = (profileData?.kycStatus || safeUser.kycStatus) !== 'APPROVED';

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <Dashboard fontSize="medium" />,
      path: '/merchant',
      disabled: false
    },
    {
      text: 'KYC Documents',
      icon: <Description fontSize="medium" />,
      path: '/merchant/kyc',
      disabled: false
    },
    {
      text: 'Fund Request',
      icon: <AccountBalanceWallet fontSize="medium" />,
      path: '/merchant/wallet',
      disabled: isPendingKyc
    },
    {
      text: 'Withdrawals',
      icon: <AccountBalanceWallet fontSize="medium" />,
      path: '/merchant/withdrawals',
      disabled: isPendingKyc
    },
    {
      text: 'APIs',
      icon: <Api fontSize="medium" />,
      path: '/merchant/apis',
      disabled: isPendingKyc
    },
    {
      text: 'Developer',
      icon: <Settings fontSize="medium" />,
      path: '/merchant/developer',
      disabled: isPendingKyc
    },
    {
      text: 'Ledger',
      icon: <Assessment fontSize="medium" />,
      path: '/merchant/reports',
      disabled: isPendingKyc
    },
    // {
    //   text: 'Gateway Reports',
    //   icon: <Assessment fontSize="small" />,
    //   path: '/merchant/gateway-reports',
    //   disabled: isPendingKyc
    // },
    {
      text: 'Services Report',
      icon: <Assessment fontSize="medium" />,
      path: '/merchant/services-report',
      disabled: isPendingKyc
    },
    {
      text: 'Support',
      icon: <SupportAgent fontSize="medium" />,
      path: '/merchant/support',
      disabled: false
    },
    {
      text: 'Profile',
      icon: <Person fontSize="medium" />,
      path: '/merchant/profile',
      disabled: false
    },
  ];

  const SidebarContent = () => (
    <Box sx={{ height: '100%', background: 'linear-gradient(180deg, #060d1e 0%, #071222 50%, #060d1e 100%)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(6,182,212,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(6,182,212,0.03)' }}>
        <Box
          component="img"
          src="/payntric_logo.png"
          alt="PayNTric Logo"
          sx={{
            height: 32,
            width: 'auto',
            filter: 'drop-shadow(0 0 8px rgba(6,182,212,0.4))',
          }}
        />

        {isPendingKyc && (
          <Chip
            icon={<Warning sx={{ color: 'inherit !important' }} />}
            label="KYC"
            size="small"
            color="warning"
          />
        )}
      </Box>

      {/* Menu Items */}
      <List sx={{ px: 1, flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              disabled={item.disabled}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 1.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(6, 182, 212, 0.12)',
                  color: 'white',
                  borderLeft: '3px solid #06b6d4',
                  pl: '5px',
                },
                '&:hover': {
                  backgroundColor: item.disabled ? 'transparent' : 'rgba(6, 182, 212, 0.07)',
                },
                '&.Mui-disabled': {
                  opacity: 0.35,
                }
              }}
            >
              <ListItemIcon sx={{
                minWidth: 40,
                color: location.pathname === item.path ? '#06b6d4' : 'rgba(125, 211, 252, 0.65)'
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: location.pathname === item.path ? 700 : 400,
                    color: location.pathname === item.path ? '#67e8f9' : 'rgba(203, 232, 248, 0.85)',
                    fontSize: '0.875rem',
                    letterSpacing: location.pathname === item.path ? '0.01em' : 'normal',
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mx: 2, mb: 2, backgroundColor: 'rgba(6,182,212,0.15)' }} />

      {/* User Info */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          backgroundColor: 'rgba(6,182,212,0.07)',
          border: '1px solid rgba(6,182,212,0.15)',
          borderRadius: 2,
          mb: 1
        }}>
          <Avatar sx={{ background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)', width: 32, height: 32, fontSize: '0.875rem', fontWeight: 700 }}>
            {safeUser.merchantName?.charAt(0) || 'M'}
          </Avatar>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }} noWrap>
              {safeUser.merchantName || 'Merchant'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }} noWrap display="block">
              {safeUser.merchantId || 'ID'}
            </Typography>
          </Box>
        </Box>

        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 1.5,
            color: 'rgba(203, 232, 248, 0.6)',
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.12)',
              color: '#fca5a5'
            }
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.9rem' }} primary="Logout" />
        </ListItemButton>
      </Box>
    </Box>
  );

  // Alias for backward compatibility with any stale references
  const drawerContent = <SidebarContent />;
  const DrawerContent = drawerContent;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Top Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          zIndex: 1300,
          width: isMobile ? '100%' : 'calc(100% - 280px)',
          ml: isMobile ? 0 : '280px',
          height: 64
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64 }}>
          {/* Left Section - Welcome Message */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1, color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="subtitle1" fontWeight={600} color="text.primary" noWrap>
              {isMobile ? "PayNTric" : `Welcome, ${safeUser.merchantName || safeUser.name || "User"}`}
            </Typography>
          </Box>

          {/* Right Section - Profile Only */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Profile Section */}
            <Box sx={{ position: 'relative' }}>
              <Tooltip title="Profile & Settings">
                <IconButton
                  onClick={handleProfileMenuOpen}
                  sx={{
                    padding: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    sx={{
                      '& .MuiBadge-dot': {
                        backgroundColor: safeUser.kycStatus === 'APPROVED' ? '#10b981' : '#f59e0b',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        border: '2px solid white'
                      }
                    }}
                  >
                    <Avatar sx={{
                      bgcolor: "primary.main",
                      width: 32,
                      height: 32,
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}>
                      {profileData?.merchantName ? (
                        profileData.merchantName.charAt(0).toUpperCase()
                      ) : (
                        safeUser.merchantName ? (
                          safeUser.merchantName.charAt(0).toUpperCase()
                        ) : (
                          <Person fontSize="16" />
                        )
                      )}
                    </Avatar>
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* Profile Dropdown Menu */}
              <Menu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={handleProfileMenuClose}
                PaperProps={{
                  elevation: 8,
                  sx: {
                    mt: 1.5,
                    minWidth: 280,
                    borderRadius: 2,
                    overflow: 'visible',
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                  }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                {/* Profile Info Header */}
                <Box sx={{
                  p: 3,
                  borderBottom: 1,
                  borderColor: 'divider',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                }}>
                  <Box display="flex" alignItems="center" gap={3}>
                    <Avatar sx={{
                      bgcolor: "primary.main",
                      width: 48,
                      height: 48,
                      fontSize: "1.125rem",
                      fontWeight: 700,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}>
                      {profileData?.merchantName ? (
                        profileData.merchantName.charAt(0).toUpperCase()
                      ) : (
                        safeUser.merchantName ? (
                          safeUser.merchantName.charAt(0).toUpperCase()
                        ) : (
                          <Person />
                        )
                      )}
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight={600} color="text.primary">
                        {profileData?.merchantName || safeUser.merchantName || "User"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {profileData?.email || safeUser.email || "No email"}
                      </Typography>
                      <Typography variant="caption" color="primary.main" sx={{ mt: 0.5 }}>
                        ID: {profileData?.merchantId || safeUser.merchantId || "No ID"}
                      </Typography>
                      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          label={profileData?.kycStatus || safeUser.kycStatus || "PENDING"}
                          size="small"
                          color={profileData?.kycStatus === "APPROVED" ? "success" : "warning"}
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem"
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Divider />

                <MenuItem
                  onClick={handleProfileClick}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <ListItemIcon>
                    <AccountCircle />
                  </ListItemIcon>
                  <ListItemText primary="View Profile" />
                </MenuItem>

                <MenuItem
                  onClick={() => { navigate('/merchant/kyc'); handleProfileMenuClose(); }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <ListItemIcon>
                    <Assessment />
                  </ListItemIcon>
                  <ListItemText primary="KYC Status" />
                </MenuItem>

                <MenuItem
                  onClick={() => { navigate('/merchant/wallet'); handleProfileMenuClose(); }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <ListItemIcon>
                    <AccountBalanceWallet />
                  </ListItemIcon>
                  <ListItemText primary="My Wallet" />
                </MenuItem>

                <Divider />

                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    py: 1.5,
                    px: 2,
                    color: 'error.main'
                  }}
                >
                  <ListItemIcon>
                    <Logout />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', mt: '64px' }}>
        {/* Sidebar Drawer */}
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
              borderRight: 'none',
              boxShadow: isMobile ? '8px 0 24px rgba(0,0,0,0.15)' : 'none'
            },
            display: { xs: 'block', md: 'block' },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            ml: isMobile ? 0 : '280px', // Sidebar width
            mt: 0,
            backgroundColor: '#f5f5f5',
            minHeight: 'calc(100vh - 64px)', // Account for top bar
            overflow: 'hidden', // Prevent horizontal overflow
            width: isMobile ? '100%' : 'calc(100vw - 280px)' // Constrain width to viewport minus sidebar
          }}
        >
          <Box sx={{
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SimpleMerchantLayout;
